/**
 * GeoDirectory Popup Handler
 *
 * Unified popup system that works with both Google Maps and Leaflet.
 * Uses Leaflet-style API for consistency and better styling control.
 */

export class Popup {
	/**
	 * Create and open a popup for a marker.
	 *
	 * @param {object} mapProvider - The map provider instance (GoogleMapProvider or OSMMapProvider)
	 * @param {object} marker - The marker object
	 * @param {string} content - HTML content to display
	 */
	static openPopup(mapProvider, marker, content) {

		if (!mapProvider || !marker) {
			return;
		}

		// Use provider-specific popup implementation
		if (typeof mapProvider.openPopup === 'function') {
			mapProvider.openPopup(marker, content);
		} else {
		}
	}

	/**
	 * Fetch popup content from REST API and open popup.
	 *
	 * @param {object} mapProvider - The map provider instance
	 * @param {object} marker - The marker object
	 * @param {number} postId - The post ID to fetch content for
	 * @param {object} options - Additional options (restUrl, restNonce, etc.)
	 */
	static async fetchAndOpenPopup(mapProvider, marker, postId, options = {}) {

		const restUrl = options.restUrl || window.geodir_map_params?.rest_url || '/wp-json/geodir/v3';
		const restNonce = options.restNonce || window.geodir_map_params?.rest_nonce || '';

		// Show loading indicator
		this.openPopup(mapProvider, marker, '<div class="text-center p-3"><div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden">Loading...</span></div></div>');

		try {
			// Fetch popup content from REST API
			const url = `${restUrl}/markers/${postId}`;

			const response = await fetch(url, {
				headers: {
					'X-WP-Nonce': restNonce
				}
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();

			if (data.html) {
				// Open popup with fetched content
				this.openPopup(mapProvider, marker, data.html);

				// Initialize GeoDir components in the popup content
				if (window.GeoDir && typeof window.GeoDir.init === 'function') {
					// Small delay to ensure popup DOM is ready
					setTimeout(() => {
						window.GeoDir.init();
					}, 50);
				}
			} else {
				throw new Error('No HTML content in response');
			}
		} catch (error) {
			this.openPopup(mapProvider, marker, '<div class="alert alert-danger m-2">Failed to load listing details.</div>');
		}
	}

	/**
	 * Close any open popups.
	 *
	 * @param {object} mapProvider - The map provider instance
	 */
	static closePopup(mapProvider) {
		if (mapProvider && typeof mapProvider.closePopup === 'function') {
			mapProvider.closePopup();
		}
	}
}

